import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Leaf, Check, ChevronDown, ChevronUp, Clock, Sparkles, Award, Save, BookOpen, Loader2
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const HabitModuleDetailPage = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { token } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [reflections, setReflections] = useState({});
  const [savingReflection, setSavingReflection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    introduction: true,
    concepts: false,
    reflection: false,
    practice: false
  });

  useEffect(() => {
    fetchModule();
    fetchReflections();
  }, [moduleId]);

  const fetchReflections = async () => {
    try {
      const response = await axios.get(`${API}/reflections/habits/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.reflections) {
        const reflectionMap = {};
        response.data.reflections.forEach(r => {
          reflectionMap[r.prompt_index] = r.content;
        });
        setReflections(reflectionMap);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Failed to load reflections:", error);
      }
    }
  };

  const saveReflection = async (promptIndex, prompt) => {
    const content = reflections[promptIndex];
    if (!content?.trim()) return;
    
    setSavingReflection(promptIndex);
    try {
      await axios.post(`${API}/reflections/save`, {
        module_type: 'healthy_habits',
        module_id: parseInt(moduleId),
        prompt_index: promptIndex,
        prompt_text: prompt,
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reflection saved!");
    } catch (error) {
      toast.error("Failed to save reflection");
    } finally {
      setSavingReflection(null);
    }
  };

  const goToJournalWithPrompt = (prompt) => {
    navigate(`/journal?prompt=${encodeURIComponent(prompt)}`);
  };

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/healthy-habits/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModule(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Premium subscription required for this module");
        navigate("/healthy-habits");
      } else {
        toast.error("Module not found");
        navigate("/healthy-habits");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(
        `${API}/healthy-habits/modules/${moduleId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setModule({ ...module, is_completed: true });
      
      if (response.data.badge_awarded) {
        toast.success("🏆 Congratulations! You've earned the Healthy Habits Mastery badge!");
      } else {
        toast.success(`✨ Module completed! ${response.data.total_completed}/20 modules done.`);
      }
    } catch (error) {
      toast.error("Unable to mark module as complete");
    } finally {
      setCompleting(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
            <Leaf className="w-8 h-8 text-green-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  if (!module) return null;

  return (
    <div className="min-h-screen hero-gradient" data-testid="habit-module-detail-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/healthy-habits")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Modules
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Module {module.id}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{module.estimated_time}</span>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <Leaf className="w-8 h-8 text-green-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            {module.title}
          </h1>
          <p className="text-lg text-muted-foreground">
            {module.subtitle}
          </p>
          {module.is_completed && (
            <div className="inline-flex items-center gap-2 mt-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Completed</span>
            </div>
          )}
        </div>

        {/* Introduction Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('introduction')}
            className="w-full p-6 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Introduction</h2>
            </div>
            {expandedSections.introduction ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          {expandedSections.introduction && (
            <CardContent className="px-6 pb-6 pt-0">
              <div className="prose prose-lg dark:prose-invert max-w-none">
                {module.introduction.split('\n\n').map((paragraph, i) => (
                  <p key={`intro-${i}`} className="text-foreground leading-relaxed mb-4">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Key Concepts Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('concepts')}
            className="w-full p-6 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Key Concepts</h2>
            </div>
            {expandedSections.concepts ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          {expandedSections.concepts && (
            <CardContent className="px-6 pb-6 pt-0">
              <div className="space-y-6">
                {module.key_concepts.map((concept, i) => (
                  <div key={concept.title || `concept-${i}`} className="border-l-4 border-green-500/50 pl-4">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {concept.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {concept.description}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Reflection Prompts Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('reflection')}
            className="w-full p-6 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Reflection Prompts</h2>
            </div>
            {expandedSections.reflection ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          {expandedSections.reflection && (
            <CardContent className="px-6 pb-6 pt-0">
              <p className="text-muted-foreground mb-4 italic">
                Take time to journal or reflect on these questions:
              </p>
              <div className="space-y-6">
                {module.reflection_prompts.map((prompt, i) => (
                  <div key={`prompt-${i}`} className="bg-emerald-50/50 dark:bg-emerald-950/20 rounded-xl p-5">
                    <div className="flex gap-3 mb-4">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center text-sm font-medium">
                        {i + 1}
                      </span>
                      <p className="text-foreground leading-relaxed italic">{prompt}</p>
                    </div>
                    
                    {/* Reflection Input */}
                    <div className="ml-9">
                      <Textarea
                        placeholder="Write your reflection here..."
                        value={reflections[i] || ""}
                        onChange={(e) => setReflections(prev => ({ ...prev, [i]: e.target.value }))}
                        className="min-h-[100px] rounded-xl mb-3 bg-white/50 dark:bg-white/5"
                        data-testid={`habit-reflection-textarea-${i}`}
                      />
                      <div className="flex gap-3 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => saveReflection(i, prompt)}
                          disabled={savingReflection === i || !reflections[i]?.trim()}
                          className="rounded-lg bg-emerald-600 hover:bg-teal-700"
                          data-testid={`habit-save-reflection-btn-${i}`}
                        >
                          {savingReflection === i ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <Save className="w-4 h-4 mr-1" />
                          )}
                          Save Reflection
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => goToJournalWithPrompt(prompt)}
                          className="rounded-lg"
                          data-testid={`habit-expand-journal-btn-${i}`}
                        >
                          <BookOpen className="w-4 h-4 mr-1" />
                          Expand in Journal
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Practice Section */}
        <Card className="glass-card rounded-2xl border-0 mb-8 overflow-hidden bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20">
          <button
            onClick={() => toggleSection('practice')}
            className="w-full p-6 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Practice Exercise</h2>
            </div>
            {expandedSections.practice ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
          {expandedSections.practice && (
            <CardContent className="px-6 pb-6 pt-0">
              <div className="bg-white/50 dark:bg-white/5 rounded-xl p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {module.practice.title}
                </h3>
                <p className="text-foreground leading-relaxed mb-4">
                  {module.practice.description}
                </p>
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <Clock className="w-4 h-4" />
                  <span>{module.practice.duration}</span>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Complete Button */}
        <div className="text-center">
          {module.is_completed ? (
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-6 py-3 rounded-full">
                <Check className="w-5 h-5" />
                <span className="font-medium">Module Completed</span>
              </div>
              <p className="text-muted-foreground">
                Great work! Continue to the next module to build your healthy habits.
              </p>
            </div>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={completing}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-xl"
            >
              {completing ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Completing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Mark as Complete
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-12 pt-8 border-t border-border/50">
          {module.id > 1 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/healthy-habits/${module.id - 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous Module
            </Button>
          )}
          <div className="flex-1" />
          {module.id < 20 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/healthy-habits/${module.id + 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              Next Module
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default HabitModuleDetailPage;
